import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTheme, IconButton, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';

export const ACCEPTED_RECEIPT_TYPES = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Drag-and-drop attachment picker for receipt files (PDF, PNG, JPEG). Keeps
// its own dropzone wiring but stores the file list in the parent so the
// generate step can read it alongside the rest of the form.
const ReceiptDropzone = ({ files, onFilesAdded, onRemoveFile }) => {
  const theme = useTheme();

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length > 0) onFilesAdded(acceptedFiles);
    },
    [onFilesAdded]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_RECEIPT_TYPES,
  });

  const dropzoneStyle = {
    border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.brand.border}`,
    borderRadius: '10px',
    padding: '28px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: isDragActive ? theme.palette.brand.mist : theme.palette.background.paper,
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
  };

  const fileRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    padding: '10px 14px',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.brand.border}`,
    borderRadius: '8px',
    marginTop: '8px',
  };

  return (
    <div>
      <div {...getRootProps()} style={dropzoneStyle}>
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: '28px', color: theme.palette.primary.main, marginBottom: '6px' }} />
        <Typography
          style={{
            fontFamily: theme.typography.paragraphFontFamily,
            fontSize: '14px',
            color: theme.palette.text.primary,
          }}
        >
          {isDragActive ? 'Drop receipts here' : 'Drag and drop receipts here, or click to browse'}
        </Typography>
        <Typography
          style={{
            fontFamily: theme.typography.paragraphFontFamily,
            fontSize: '12px',
            color: theme.palette.text.secondary,
            marginTop: '2px',
          }}
        >
          PDF, PNG, or JPEG
        </Typography>
      </div>

      {files.length > 0 && (
        <div style={{ marginTop: '4px' }}>
          {files.map((file, index) => (
            <div key={`${file.name}-${file.lastModified}-${index}`} style={fileRowStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                {file.type === 'application/pdf' ? (
                  <DescriptionIcon sx={{ color: theme.palette.brand.brass, flexShrink: 0 }} />
                ) : (
                  <ImageIcon sx={{ color: theme.palette.brand.brass, flexShrink: 0 }} />
                )}
                <Typography
                  noWrap
                  style={{
                    fontFamily: theme.typography.paragraphFontFamily,
                    fontSize: '13px',
                    color: theme.palette.text.primary,
                  }}
                >
                  {file.name}
                </Typography>
                <Typography
                  style={{
                    fontFamily: theme.typography.paragraphFontFamily,
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                    flexShrink: 0,
                  }}
                >
                  {formatFileSize(file.size)}
                </Typography>
              </div>
              <IconButton size="small" onClick={() => onRemoveFile(index)} aria-label={`Remove ${file.name}`}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReceiptDropzone;
